import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AuctionCard } from "@/components/auction/AuctionCard";
import { HomeSearch } from "@/components/HomeSearch";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Lock, ShieldCheck, Gavel, Timer, Users, ArrowRight, EyeOff, BadgeCheck,
    Trophy, Store, Package, Headphones, PlayCircle, TrendingUp, Sparkles, Ban, Zap,
    Watch, Palette, Cpu, Shirt, Armchair, Car, BookOpen, Star, Quote, Check,
} from "lucide-react";

const ICONS = { Watch, Palette, Cpu, Shirt, Armchair, Car, Trophy, BookOpen };

const img = (id, w = 800) => `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

// Lifestyle imagery — happy customers / people enjoying their wins (verified URLs).
const SOCIAL_PROOF = [
    { id: "1556745757-8d76bdb6984b", alt: "Happy customer with her winning purchase", cap: "Won my dream watch — fair and square." },
    { id: "1604881991720-f91add269bed", alt: "Buyer celebrating an auction win on her phone", cap: "One honest bid. No stress. Huge win." },
    { id: "1521146764736-56c929d59c83", alt: "Friends excited about a great deal", cap: "Finally, an auction that feels fair." },
    { id: "1551836022-deb4988cc6c0", alt: "Customer celebrating a successful bid", cap: "Beat the bidding wars for good." },
];
const BUYER_THUMBS = ["1607082348824-0a96f2a4b9da", "1543269865-cbf427effbad", "1494790108377-be9c29b29330"];
// Seller fulfilment lifestyle image — packing an order for shipment (verified URL).
const SELLER_PACKING = "1586528116311-ad8dd3c8310d";
// Winner-holding-product lifestyle image for the testimonials section (verified URL).
const WINNER_IMG = "1556742502-ec7c0e9f34b1";

const TRUST_BAR = [
    { icon: Trophy, title: "Thousands", sub: "of Happy Winners" },
    { icon: Store, title: "Verified", sub: "Top-Rated Sellers" },
    { icon: Package, title: "Secure Payments", sub: "& Fast Shipping" },
    { icon: Headphones, title: "24/7 Support", sub: "We're Here For You" },
];

export default function Home() {
    const [featured, setFeatured] = useState(null);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/auctions/featured").then(({ data }) => setFeatured(data.ending_soon)).catch(() => setFeatured([]));
        api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    }, []);

    return (
        <div>
            {/* Hero (dark) */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_15%_0%,rgba(46,139,255,0.22)_0%,rgba(46,139,255,0)_60%),radial-gradient(50%_50%_at_90%_10%,rgba(240,180,41,0.10)_0%,rgba(240,180,41,0)_55%)]" />
                <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
                    <div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                            <EyeOff className="h-3.5 w-3.5 text-electric" /> Your bid stays sealed &mdash; always
                        </span>
                        <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                            One sealed bid.<br /><span className="text-brass italic">One fair shot.</span>
                        </h1>
                        <p className="mt-5 max-w-lg text-base text-muted-foreground">
                            Win incredible products through ethical, <span className="text-foreground">blind bidding</span> &mdash; no sniping, no bidding wars, no games.
                        </p>

                        <ul className="mt-7 space-y-2.5" data-testid="hero-checklist">
                            {["One Private Bid", "No Visible Competitors", "No Hidden Reserves", "Verified Sellers Only"].map((item) => (
                                <li key={item} className="flex items-center gap-2.5 text-sm font-medium">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success"><Check className="h-3.5 w-3.5" /></span>
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Button asChild size="lg" className="rounded-[10px] font-semibold" data-testid="hero-browse-cta">
                                <Link to="/auctions">Browse Auctions <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" className="rounded-[10px] border-electric/50 text-foreground hover:bg-electric/10" data-testid="hero-how-cta">
                                <Link to="/trust-safety"><PlayCircle className="mr-1.5 h-4 w-4 text-electric" /> How It Works</Link>
                            </Button>
                        </div>
                    </div>

                    <div className="grid w-full max-w-md gap-4 justify-self-center lg:justify-self-end">
                        {featured === null ? (
                            <Skeleton className="h-[460px] w-full rounded-xl" />
                        ) : featured.length ? (
                            featured.slice(0, 1).map((a) => <AuctionCard key={a.id} a={a} />)
                        ) : (
                            <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground">No live auctions yet.</div>
                        )}
                    </div>
                </div>

                {/* Trust bar */}
                <div className="border-t border-border bg-card/40">
                    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-4">
                        {TRUST_BAR.map((t) => {
                            const Icon = t.icon;
                            return (
                                <div key={t.title} className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-brass"><Icon className="h-5 w-5" /></span>
                                    <div className="leading-tight">
                                        <p className="font-display text-sm font-bold">{t.title}</p>
                                        <p className="text-xs text-muted-foreground">{t.sub}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Advanced product search */}
            <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 sm:pt-14" data-testid="home-search-section">
                <div className="mb-4">
                    <h2 className="font-display text-2xl font-bold tracking-tight">Find your next win</h2>
                    <p className="mt-1 text-sm text-muted-foreground">Search by product, then refine by category, specialty, price, condition, and seller rating.</p>
                </div>
                <HomeSearch />
            </section>

            {/* Ending soon (dark) */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                <div className="mb-8 flex items-end justify-between">
                    <div>
                        <h2 className="font-display text-2xl font-bold tracking-tight">Ending soon</h2>
                        <p className="mt-1 text-sm text-muted-foreground">Place your single sealed bid before time runs out.</p>
                    </div>
                    <Button asChild variant="ghost" className="rounded-[10px] text-electric"><Link to="/auctions">View all</Link></Button>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {featured === null
                        ? ["s1", "s2", "s3", "s4"].map((k) => <Skeleton key={k} className="h-96 w-full rounded-xl" />)
                        : featured.slice(0, 8).map((a) => <AuctionCard key={a.id} a={a} />)}
                </div>
            </section>

            {/* How it works — WHITE */}
            <HowItWorks />

            {/* Auction mechanics (dark) */}
            <AuctionMechanicsSection />

            {/* Categories (dark) */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
                <h2 className="mb-8 font-display text-2xl font-bold tracking-tight">Browse by category</h2>
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                    {categories.map((c) => {
                        const Icon = ICONS[c.icon] || Gavel;
                        return (
                            <Link
                                key={c.slug}
                                to={`/auctions?category=${c.slug}`}
                                data-testid={`category-tile-${c.slug}`}
                                className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-[1px] hover:border-electric/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                            >
                                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-electric"><Icon className="h-5 w-5" /></span>
                                <div>
                                    <p className="font-display text-sm font-bold">{c.name}</p>
                                    <p className="text-xs text-muted-foreground">{c.open_count} open</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* Fair-market pricing (dark) */}
            <FairMarketSection />

            {/* Sellers + Buyers benefits — WHITE */}
            <BenefitsSection />

            {/* Social proof — lifestyle imagery (light gray) */}
            <SocialProofSection />

            {/* Testimonials (dark) */}
            <TestimonialsSection />

            {/* Final CTA band (dark) */}
            <FinalCTASection />

            {/* Light breather band before footer */}
            <div className="bg-slate-50">
                <div className="mx-auto max-w-5xl px-4 py-14 text-center sm:px-6">
                    <p className="font-display text-lg font-semibold text-[#1A1A1A] sm:text-xl">
                        Join thousands of happy winners on <span className="text-amber-600">OmniAuc</span>.
                    </p>
                    <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
                        Create your free account in under a minute and place your first sealed bid today.
                    </p>
                </div>
            </div>
        </div>
    );
}

// ---------------- White: How it works ----------------
function HowItWorks() {
    const steps = [
        { icon: ShieldCheck, title: "1. Verify your account", lines: ["Buyers verify email + phone.", "Sellers complete full identity verification for trust and safety."] },
        { icon: Lock, title: "2. Place one sealed bid", lines: ["Submit a single private bid.", "No one — not even the seller — can see other bids."] },
        { icon: Trophy, title: "3. Winner revealed at close", lines: ["When the timer ends or the bidder limit is reached, the highest sealed bid wins automatically."] },
    ];
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
                <div className="mx-auto max-w-2xl text-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                        <Gavel className="h-3.5 w-3.5 text-amber-600" /> Simple &amp; transparent
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">How blind bidding works</h2>
                    <p className="mt-3 text-sm text-slate-500">Three steps from sign-up to a fair win — no games, no guesswork.</p>
                </div>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {steps.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0A1226]/[0.05] text-[#16264a]"><Icon className="h-5 w-5" /></span>
                                <h3 className="mt-5 font-display text-lg font-bold text-[#1A1A1A]">{s.title}</h3>
                                {s.lines.map((line) => (
                                    <p key={line} className="mt-2 text-sm leading-relaxed text-slate-600">{line}</p>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ---------------- Dark: Auction mechanics ----------------
function AuctionMechanicsSection() {
    const ways = [
        { icon: Timer, title: "Timer-Based Ending", lines: ["Every auction has a countdown.", "When it hits zero, bidding closes instantly."] },
        { icon: Users, title: "Bidder-Limit Ending", lines: ["Sellers can set a max number of bidders.", "Once the cap is reached, the auction ends immediately."] },
    ];
    return (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
            <h2 className="text-center font-display text-2xl font-bold tracking-tight sm:text-3xl">Two ways auctions end</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
                Every listing closes fairly and automatically &mdash; the moment time runs out or the bidder cap fills.
            </p>
            <div className="mt-12 grid gap-5 sm:grid-cols-2">
                {ways.map((w) => {
                    const Icon = w.icon;
                    return (
                        <div key={w.title} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-7">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-electric"><Icon className="h-5 w-5" /></span>
                            <div>
                                <h3 className="font-display text-lg font-bold">{w.title}</h3>
                                {w.lines.map((line) => (
                                    <p key={line} className="mt-1 text-sm text-muted-foreground">{line}</p>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

// ---------------- Dark: Fair-market pricing ----------------
const FAIR_MARKET_CARDS = [
    { icon: TrendingUp, title: "No nudging", text: "No incremental raises. Your first number is your real number." },
    { icon: Users, title: "No anchoring", text: "Without a visible 'high bid', nobody is pushed up or scared off." },
    { icon: Ban, title: "No 'one dollar more'", text: "You can't be edged out by a single increment at the last second." },
    { icon: Zap, title: "Just the real market", text: "Every sealed bid is honest willingness-to-pay — nothing else." },
];

function FairMarketSection() {
    return (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                <div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                        <Zap className="h-3.5 w-3.5 text-electric" /> Fair-market pricing
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        Why blind bidding creates <span className="text-brass">true market value.</span>
                    </h2>
                    <p className="mt-4 text-muted-foreground">
                        When you can only bid once, you bid honestly &mdash; and that single number reflects your real <span className="text-foreground">willingness-to-pay</span>.
                    </p>
                    <p className="mt-6 border-l-2 border-brass pl-4 font-display text-lg italic text-foreground">
                        Open auctions measure reflexes. Blind auctions measure value.
                    </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    {FAIR_MARKET_CARDS.map((c) => {
                        const Icon = c.icon;
                        return (
                            <div key={c.title} className="rounded-xl border border-border bg-card p-5">
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-electric"><Icon className="h-4 w-4" /></span>
                                <h3 className="mt-3 font-display font-bold">{c.title}</h3>
                                <p className="mt-1 text-sm text-muted-foreground">{c.text}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ---------------- White: Sellers + Buyers benefits ----------------
const SELLER_BENEFITS = [
    [TrendingUp, "Capture true willingness-to-pay", "Blind bidding reveals the real value buyers place on your item — not just one increment above the next person."],
    [Timer, "Set your own rules", "Choose duration and bidder cap. Hit the cap and close early."],
    [Ban, "No reserve headaches", "Skip artificial floors. Real demand sets the price."],
    [Sparkles, "AI-generated descriptions", "Create polished, honest listings in one click."],
];
const BUYER_BENEFITS = [
    [Zap, "Win on value, not speed", "No bots. No sniping. No bidding wars."],
    [ShieldCheck, "A genuinely fair shot", "One bid per buyer means casual bidders compete equally with pros."],
    [Lock, "Privacy by default", "Your bid is never shown to sellers or other buyers."],
    [EyeOff, "No last-second games", "Your honest number stands — no one can see or beat it."],
    [BadgeCheck, "Buy with confidence", "Verified sellers. Secure payments. Real dispute support."],
];

function BenefitList({ items, accent }) {
    return (
        <ul className="mt-5 space-y-3 text-sm">
            {items.map(([Icon, t, d]) => (
                <li key={t} className="flex items-start gap-3">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${accent}`} />
                    <span><span className="font-semibold text-[#1A1A1A]">{t}.</span> <span className="text-slate-600">{d}</span></span>
                </li>
            ))}
        </ul>
    );
}

function BenefitsSection() {
    return (
        <section className="bg-white">
            <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Sellers */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:p-8">
                        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600"><Store className="h-5 w-5" /></span>
                        <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#1A1A1A]">Why sellers earn more on OmniAuc</h3>
                        <p className="mt-2 text-sm text-slate-600">Blind bidding surfaces what buyers will truly pay &mdash; often more than a public auction ever reveals.</p>
                        {/* Seller fulfilment image */}
                        <div className="mt-5 aspect-[16/9] w-full overflow-hidden rounded-xl border border-slate-200">
                            <img src={img(SELLER_PACKING, 800)} alt="Seller packing a sold item for shipment" loading="lazy" className="h-full w-full object-cover" />
                        </div>
                        <BenefitList items={SELLER_BENEFITS} accent="text-amber-600" />
                        <Button asChild className="mt-7 rounded-[10px] font-semibold"><Link to="/dashboard/seller">Start selling &mdash; it takes minutes</Link></Button>
                    </div>
                    {/* Buyers */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:p-8">
                        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0A1226]/[0.05] text-[#16264a]"><Trophy className="h-5 w-5" /></span>
                        <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-[#1A1A1A]">Why buyers love OmniAuc</h3>
                        <p className="mt-2 text-sm text-slate-600">No bots, no bidding wars, no 2&nbsp;a.m. sniping. Just one fair shot at something you want.</p>
                        {/* Lifestyle thumbnails */}
                        <div className="mt-5 grid grid-cols-3 gap-3">
                            {BUYER_THUMBS.map((id) => (
                                <div key={id} className="aspect-[4/3] overflow-hidden rounded-xl border border-slate-200">
                                    <img src={img(id, 400)} alt="Happy OmniAuc buyer" loading="lazy" className="h-full w-full object-cover" />
                                </div>
                            ))}
                        </div>
                        <BenefitList items={BUYER_BENEFITS} accent="text-[#16264a]" />
                        <Button asChild variant="outline" className="mt-7 rounded-[10px] border-[#16264a]/30 text-[#16264a] hover:bg-[#16264a]/[0.05]"><Link to="/auctions">Browse auctions <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------------- Light gray: Social proof (happy customers) ----------------
function SocialProofSection() {
    return (
        <section className="bg-slate-50" data-testid="social-proof-section">
            <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
                <div className="mx-auto max-w-2xl text-center">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> Loved by real buyers
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#1A1A1A] sm:text-4xl">
                        Real people. <span className="text-amber-600">Real wins.</span>
                    </h2>
                    <p className="mt-3 text-sm text-slate-500">
                        Everyday buyers landing the products they love at fair-market prices &mdash; and loving every minute of it.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-2 gap-5 lg:grid-cols-4">
                    {SOCIAL_PROOF.map((p) => (
                        <figure key={p.id} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(16,24,40,0.06)]" data-testid="social-proof-card">
                            <div className="aspect-[4/5] w-full overflow-hidden">
                                <img src={img(p.id, 600)} alt={p.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                            </div>
                            <figcaption className="p-4">
                                <span className="flex items-center gap-1 text-amber-500" aria-label="5 out of 5 stars">
                                    {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" aria-hidden />)}
                                </span>
                                <p className="mt-2 text-sm font-medium leading-snug text-[#1A1A1A]">&ldquo;{p.cap}&rdquo;</p>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------------- Dark: Testimonials ----------------
const TESTIMONIALS = [
    { name: "Mandy R.", initials: "MR", tone: "bg-electric/15 text-electric", item: "iPhone 17 Pro Max", price: "$195", quote: "Mandy won an iPhone 17 Pro Max for $195." },
    { name: "Devin K.", initials: "DK", tone: "bg-brass/15 text-brass", item: "PlayStation 5", price: "$96", quote: "Devin snagged a PlayStation 5 for just $96." },
    { name: "Priya S.", initials: "PS", tone: "bg-success/15 text-success", item: "Rolex Submariner", price: "$610", quote: "Priya took home a Rolex Submariner for $610." },
    { name: "Marcus T.", initials: "MT", tone: "bg-electric/15 text-electric", item: "AirPods Pro", price: "$48", quote: "Marcus won a pair of AirPods Pro for $48." },
    { name: "Lena O.", initials: "LO", tone: "bg-brass/15 text-brass", item: "MacBook Pro 16\"", price: "$720", quote: "Lena landed a MacBook Pro 16\" for $720." },
    { name: "Theo M.", initials: "TM", tone: "bg-success/15 text-success", item: "Air Jordan 1 Retro", price: "$61", quote: "Theo grabbed a pair of Air Jordan 1s for $61." },
];

function TestimonialsSection() {
    return (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28" data-testid="testimonials-section">
            <div className="grid items-center gap-8 lg:grid-cols-[0.8fr_1fr]">
                <div className="overflow-hidden rounded-2xl border border-border shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
                    <img
                        src={img(WINNER_IMG, 900)}
                        alt="A happy winner holding the product they won on OmniAuc"
                        loading="lazy"
                        className="aspect-[4/3] h-full w-full object-cover"
                    />
                </div>
                <div className="max-w-xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                        <Trophy className="h-3.5 w-3.5 text-brass" /> Real winners, real prices
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                        One honest bid. <span className="text-brass italic">Incredible wins.</span>
                    </h2>
                    <p className="mt-3 text-sm text-muted-foreground">
                        Everyday people winning the products they love at fair-market prices &mdash; no bidding wars, no sniping, no bots.
                    </p>
                </div>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {TESTIMONIALS.map((t) => (
                    <figure
                        key={t.name}
                        data-testid="testimonial-card"
                        className="relative flex flex-col rounded-2xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1"
                    >
                        <Quote className="absolute right-5 top-5 h-7 w-7 text-border" aria-hidden />
                        <div className="flex items-center gap-1 text-brass" aria-label="5 out of 5 stars">
                            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-current" aria-hidden />)}
                        </div>
                        <blockquote className="mt-4 font-display text-lg font-semibold leading-snug">
                            &ldquo;{t.quote}&rdquo;
                        </blockquote>
                        <div className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs text-muted-foreground">
                            <BadgeCheck className="h-3.5 w-3.5 text-success" />
                            Won {t.item} for <span className="font-semibold text-brass">{t.price}</span>
                        </div>
                        <figcaption className="mt-5 flex items-center gap-3 border-t border-border pt-4">
                            <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold ${t.tone}`}>{t.initials}</span>
                            <div>
                                <p className="text-sm font-semibold">{t.name}</p>
                                <p className="text-xs text-muted-foreground">Verified winner</p>
                            </div>
                        </figcaption>
                    </figure>
                ))}
            </div>
        </section>
    );
}

// ---------------- Dark: Final CTA ----------------
function FinalCTASection() {
    return (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
            <div className="noise-overlay relative overflow-hidden rounded-2xl border border-border p-10 text-center sm:p-14" style={{ background: "linear-gradient(135deg, hsl(221 55% 14%), hsl(212 90% 26%))" }}>
                <div className="relative z-10">
                    <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Ready for a fair shot?</h2>
                    <p className="mx-auto mt-3 max-w-xl text-white/80">One sealed bid. No sniping. No reserves. Just the real market price &mdash; and a real chance to win.</p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="rounded-[10px] font-semibold" data-testid="final-cta-browse"><Link to="/auctions">Browse Auctions</Link></Button>
                        <Button asChild size="lg" variant="outline" className="rounded-[10px] border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="final-cta-sell"><Link to="/dashboard/seller">Start Selling</Link></Button>
                    </div>
                    <p className="mt-6 text-xs text-white/60">OmniAuc &mdash; where the best bid wins, not the fastest finger.</p>
                </div>
            </div>
        </section>
    );
}
